import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, ClickAwayListener, IconButton, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { grey } from "@mui/material/colors";
import useDebounce from "src/hooks/useDebounce";

const ListCategories = ({ warehouse = [], value, onChange, setSelectedWarehouse, hasMore, props }) => {
  const [search, setSearch] = useState();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounce = useDebounce(search || selected?.name, 600);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    setSelected(value ?? null);
    setSearch(value?.name ?? "");
  }, [value]);

  useEffect(() => {
    setPage(1);
  }, [debounce]);

  useEffect(() => {
    const skip = (page - 1) * limit;

    if (debounce) {
      setSelectedWarehouse({
        searchValue: debounce,
        warehouse: selected?.parentId === null ? undefined : selected?.parentId,
        skip,
        limit,
      });
    } else {
      setSelectedWarehouse({ skip, limit });
    }
  }, [debounce, page]);

  const anchorRef = useRef(null);
  const listRef = useRef(null);

  const handleSelect = (item) => {
    setSelected(item);
    onChange?.(item);
    setOpen(false);
  };

  const handleClear = () => {
    setSearch();
    setSelected(null);
    onChange?.(null);
    setPage(1);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleScroll = (event) => {
    const element = event.currentTarget;

    const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 20;

    if (isNearBottom && hasMore) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  const handleClickAway = () => {
    setOpen(false);

    if (selected) {
      setSearch(selected.name);
    } else {
      setSearch();
    }
  };

  const warehouseList = useMemo(() => {
    return (
      warehouse?.flatMap((item) => [
        {
          ...item,
          parentId: null,
        },
        ...(item.children || []).map((child) => ({
          ...child,
          parentId: item?._id,
        })),
      ]) || []
    );
  }, [warehouse, debounce, selected?.name]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ width: "100%", position: "relative" }}>
        <TextField
          {...props}
          ref={anchorRef}
          fullWidth
          autoComplete="off"
          value={search ?? selected?.name ?? ""}
          placeholder="Select warehouse"
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          onClick={() => {
            if (!open) {
              handleOpen();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {selected?.name || search ? (
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                    aria-label="Clear warehouse"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();

                      if (open) {
                        setOpen(false);
                      } else {
                        handleOpen();
                      }
                    }}
                    aria-label="Open warehouses"
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              width: anchorRef.current?.getBoundingClientRect().width ? `${anchorRef.current.getBoundingClientRect().width}px` : "100%",
              mt: 0.5,
              zIndex: 1300,
            }}
          >
            <Paper
              elevation={4}
              sx={{
                maxHeight: 280,
                overflow: "hidden",
              }}
            >
              <Box
                ref={listRef}
                onScroll={handleScroll}
                sx={{
                  maxHeight: 280,
                  overflowY: "auto",
                }}
              >
                {warehouseList.length > 0 ? (
                  warehouseList.map((item) => (
                    <Box
                      key={item?._id}
                      onClick={() => handleSelect(item)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        backgroundColor: selected?._id === item?._id ? grey[100] : item?.children ? grey[50] : "",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Typography variant="body2">{item?.name ?? ""}</Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                    >
                      Không có dữ liệu
                    </Typography>
                  </Box>
                )}

                {hasMore && warehouseList.length > 0 && (
                  <Box sx={{ p: 1.5, textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Scroll down to load more
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ListCategories;
